/**
 * Checkbox: a native checkbox with our box, a label and an optional description (cockatrice-ui
 * `src/ui/Checkbox.jsx`, ported to hyperscript; PLAN.md §0).
 * The whole row is the click target. `indeterminate` for "some chapters selected".
 * onChange(checked, event). Controlled: checked. Uncontrolled: defaultChecked.
 * Without a label, give it aria-label or put it in a Field / settings row with a for= label.
 */
import './css/checkbox.css';
import { createEffect, createUniqueId, splitProps } from 'solid-js';
import { useField } from './Field.ts';
import Icon from './Icon.ts';
import { component, el, spread, when } from './solid.ts';
import type { Attributes, Child, On } from './solid.ts';

export interface CheckboxProps extends Attributes {
  /** Label; the whole row becomes clickable. */
  readonly label?: Child;
  /** Second line under the label. */
  readonly description?: Child;
  /** Controlled state. */
  readonly checked?: boolean | undefined;
  /** Uncontrolled starting state. */
  readonly defaultChecked?: boolean | undefined;
  /** "Some selected", for a select-all box. */
  readonly indeterminate?: boolean | undefined;
  /** Called when toggled. */
  readonly onChange?: ((checked: boolean, event: Event) => void) | undefined;
  /** Error message under the label. */
  readonly error?: Child;
  /** Disables the checkbox. */
  readonly disabled?: boolean | undefined;
  /** Class on the wrapper. */
  readonly class?: string | undefined;
  /** Ref to the <input>. */
  readonly ref?: ((el: HTMLInputElement) => void) | undefined;
}

export default function Checkbox(props: CheckboxProps): Child {
  const [local, rest] = splitProps(props, [
    'id',
    'label',
    'description',
    'checked',
    'defaultChecked',
    'indeterminate',
    'onChange',
    'error',
    'disabled',
    'class',
    'ref',
    'aria-describedby',
  ]);
  const field = useField();
  const own = createUniqueId();
  const id = (): string => {
    const given = local.id;
    return typeof given === 'string' ? given : (field?.id ?? own);
  };
  const descId = `${own}-desc`;
  const errId = `${own}-err`;
  const describedBy = (): string | undefined => {
    const extra = local['aria-describedby'];
    return (
      [field?.describedBy, Boolean(local.description) && descId, Boolean(local.error) && errId, typeof extra === 'string' ? extra : undefined]
        .filter(Boolean)
        .join(' ') || undefined
    );
  };

  const invalid = (): boolean => Boolean(local.error) || field?.invalid === true;

  const cls = (): string =>
    ['ui-checkbox', !local.label && 'ui-checkbox--bare', invalid() && 'is-invalid', local.disabled === true && 'is-disabled', local.class].filter(Boolean).join(' ');

  const box = el(
    'span',
    { class: 'ui-checkbox__box' },
    el(
      'input',
      spread(rest, {
        ref: (node: HTMLInputElement) => {
          // Made with the input, so it always has one; it runs after the render, as an effect made in the body would.
          createEffect(() => {
            node.indeterminate = local.indeterminate === true;
          });
          local.ref?.(node);
        },
        id,
        type: 'checkbox',
        class: 'ui-checkbox__input',
        checked: () => local.checked ?? local.defaultChecked ?? false,
        disabled: () => local.disabled,
        'aria-invalid': () => (invalid() ? 'true' : undefined),
        'aria-describedby': describedBy,
        onChange: (event: On<Event, HTMLInputElement>) => local.onChange?.(event.currentTarget.checked, event),
      }),
    ),
    component(Icon, { name: 'check', size: 'sm', class: 'ui-checkbox__mark' }),
    component(Icon, { name: 'minus', size: 'sm', class: 'ui-checkbox__dash' }),
  );

  return when(
    () => Boolean(local.label),
    () =>
      el(
        'div',
        { class: cls },
        el(
          'label',
          { class: 'ui-checkbox__row', for: id },
          box,
          el(
            'span',
            { class: 'ui-checkbox__text' },
            el('span', { class: 'ui-checkbox__label' }, () => local.label),
            when(
              () => Boolean(local.description),
              () => el('span', { class: 'ui-checkbox__description', id: descId }, () => local.description),
            ),
          ),
        ),
        when(
          () => Boolean(local.error),
          () => el('p', { class: 'ui-checkbox__error', id: errId }, component(Icon, { name: 'warn', size: 'sm' }), el('span', {}, () => local.error)),
        ),
      ),
    () => el('span', { class: cls }, box),
  );
}
